import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, AlertCircle } from 'lucide-react';
import AdminNavbar from '../components/admin/AdminNavbar';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';

interface BannerSettings {
  id?: string;
  enabled: boolean;
  eyebrow_text: string;
  headline: string;
  body: string;
  primary_cta_text: string;
  primary_cta_url: string;
  secondary_cta_text: string;
  secondary_cta_url: string;
  phone_text: string;
  helper_text: string;
}

const defaultSettings: BannerSettings = {
  enabled: true,
  eyebrow_text: 'SEASONAL DIRTY WATER & INTAKE PROTECTION',
  headline: 'Protect Intakes, Pumps & Filtration During Dirty Water Season.',
  body: 'Runoff, canal turns, and river events can spike debris and turbidity fast. We help growers, districts, and operators choose the right filtration approach and protect equipment—without overbuying.',
  primary_cta_text: 'Dirty Water & Intake Protection Hub',
  primary_cta_url: '/flood-resilience',
  secondary_cta_text: 'Talk to a Specialist',
  secondary_cta_url: '/contact',
  phone_text: 'Or call (509) 901-7476',
  helper_text: 'Typical response: within 1 business day.'
};

const AdminBannerSettingsPage = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<BannerSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    }
  }, [isAuthenticated]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('seasonal_banner_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('Using default settings:', error.message);
      } else if (data) {
        setSettings(data as BannerSettings);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      if (settings.id) {
        const { error } = await supabase
          .from('seasonal_banner_settings')
          .update({
            enabled: settings.enabled,
            eyebrow_text: settings.eyebrow_text,
            headline: settings.headline,
            body: settings.body,
            primary_cta_text: settings.primary_cta_text,
            primary_cta_url: settings.primary_cta_url,
            secondary_cta_text: settings.secondary_cta_text,
            secondary_cta_url: settings.secondary_cta_url,
            phone_text: settings.phone_text,
            helper_text: settings.helper_text,
            updated_at: new Date().toISOString()
          })
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('seasonal_banner_settings')
          .insert([{
            enabled: settings.enabled,
            eyebrow_text: settings.eyebrow_text,
            headline: settings.headline,
            body: settings.body,
            primary_cta_text: settings.primary_cta_text,
            primary_cta_url: settings.primary_cta_url,
            secondary_cta_text: settings.secondary_cta_text,
            secondary_cta_url: settings.secondary_cta_url,
            phone_text: settings.phone_text,
            helper_text: settings.helper_text
          }])
          .select()
          .single();

        if (error) throw error;
        if (data) {
          setSettings({ ...settings, id: data.id });
        }
      }

      setSaveMessage({ type: 'success', text: 'Banner settings saved successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Banner Settings - Admin"
        description="Manage seasonal support banner settings"
        noIndex={true}
      />
      <AdminNavbar />

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Seasonal Support Banner Settings</h1>
            <p className="text-gray-600">Manage the evergreen seasonal support banner displayed on the homepage.</p>
          </div>

          {saveMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                saveMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              <AlertCircle size={20} />
              <span>{saveMessage.text}</span>
            </motion.div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <div className="space-y-6">
                <div className="flex items-center space-x-3 pb-6 border-b border-gray-200">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={settings.enabled}
                    onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                    className="w-5 h-5 text-ctc-blue rounded focus:ring-ctc-blue"
                  />
                  <label htmlFor="enabled" className="text-lg font-semibold text-gray-900">
                    Banner Enabled
                  </label>
                </div>

                <div>
                  <label htmlFor="eyebrow_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Eyebrow Text (Badge)
                  </label>
                  <input
                    type="text"
                    id="eyebrow_text"
                    value={settings.eyebrow_text}
                    onChange={(e) => setSettings({ ...settings, eyebrow_text: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="headline" className="block text-sm font-medium text-gray-700 mb-2">
                    Headline
                  </label>
                  <input
                    type="text"
                    id="headline"
                    value={settings.headline}
                    onChange={(e) => setSettings({ ...settings, headline: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
                    Body Text
                  </label>
                  <textarea
                    id="body"
                    value={settings.body}
                    onChange={(e) => setSettings({ ...settings, body: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="primary_cta_text" className="block text-sm font-medium text-gray-700 mb-2">
                      Primary CTA Text
                    </label>
                    <input
                      type="text"
                      id="primary_cta_text"
                      value={settings.primary_cta_text}
                      onChange={(e) => setSettings({ ...settings, primary_cta_text: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="primary_cta_url" className="block text-sm font-medium text-gray-700 mb-2">
                      Primary CTA URL
                    </label>
                    <input
                      type="text"
                      id="primary_cta_url"
                      value={settings.primary_cta_url}
                      onChange={(e) => setSettings({ ...settings, primary_cta_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="secondary_cta_text" className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary CTA Text
                    </label>
                    <input
                      type="text"
                      id="secondary_cta_text"
                      value={settings.secondary_cta_text}
                      onChange={(e) => setSettings({ ...settings, secondary_cta_text: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="secondary_cta_url" className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary CTA URL
                    </label>
                    <input
                      type="text"
                      id="secondary_cta_url"
                      value={settings.secondary_cta_url}
                      onChange={(e) => setSettings({ ...settings, secondary_cta_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Text (Optional)
                  </label>
                  <input
                    type="text"
                    id="phone_text"
                    value={settings.phone_text}
                    onChange={(e) => setSettings({ ...settings, phone_text: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    placeholder="Or call (509) 901-7476"
                  />
                </div>

                <div>
                  <label htmlFor="helper_text" className="block text-sm font-medium text-gray-700 mb-2">
                    Helper Text (Optional)
                  </label>
                  <input
                    type="text"
                    id="helper_text"
                    value={settings.helper_text}
                    onChange={(e) => setSettings({ ...settings, helper_text: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    placeholder="Typical response: within 1 business day."
                  />
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <motion.button
                    onClick={handleSave}
                    disabled={isSaving}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-8 py-4 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={20} />
                    <span>{isSaving ? 'Saving...' : 'Save Banner Settings'}</span>
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBannerSettingsPage;
